import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  ItemGroupEvent,
  ItemGroupEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/item_group_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { ItemGroupCreatedDetail } from './ItemGroupCreatedDetail';
import { ItemGroupDeletedDetail } from './ItemGroupDeletedDetail';
import { ItemGroupUpdatedDetail } from './ItemGroupUpdatedDetail';

import { useTranslations } from '@/inventory/i18n/useTranslations';

type ItemGroupEventDetailProps = {
  payload: string;
};

export const ItemGroupEventDetail = ({
  payload,
}: ItemGroupEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): ItemGroupEvent | null => {
    try {
      return fromJson(ItemGroupEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <ItemGroupCreatedDetail event={inner.value} />;
      case 'updated':
        return <ItemGroupUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <ItemGroupDeletedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.itemGroupCreated;
      case 'updated':
        return t.logs.events.itemGroupUpdated;
      case 'deleted':
        return t.logs.events.itemGroupDeleted;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@ItemGroupEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
